import React, { useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  CheckSquare,
  FileText,
  Flame,
  ArrowRight,
  Plus,
  Calendar,
  Sparkles,
  Target,
  Bookmark,
  CheckCircle2,
  Circle,
  ChevronRight,
  Globe,
  Lock,
  WalletCards,
  ArrowUpRight,
  Layers,
} from 'lucide-react';
import { formatMoney } from '../../lib/i18n';
import { StorageService } from '../../lib/storage';
import { MonthlyLedgerExpense, MonthlyLedgerIncome } from '../../types';
import { getLocalDateString, getLocalMonthString } from '../../lib/dateUtils';

export const DashboardView: React.FC = () => {
  const {
    user,
    accounts,
    transactions,
    tasks,
    notes,
    habits,
    habitLogs,
    yearlyGoals,
    personalReports,
    resources,
    currency,
    language,
    t,
    setCurrentPage,
    setActiveModal,
    toggleTask,
    toggleHabit,
    triggerConfetti,
  } = useApp();

  const isBn = language === 'bn';

  const todayStr = getLocalDateString();
  const currentMonth = getLocalMonthString();
  const currentYear = new Date().getFullYear();

  // Dynamic Greeting
  const currentHour = new Date().getHours();
  const greeting = currentHour < 12 ? 'Good morning' : currentHour < 18 ? 'Good afternoon' : 'Good evening';

  // Formatted date string
  const formattedDate = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date());

  // Total Net Balance across Accounts
  const totalBalance = useMemo(() => {
    return accounts.reduce((sum, acc) => sum + (Number(acc.balance) || 0), 0);
  }, [accounts]);

  // Read Monthly Ledger data from storage for quick summary
  const monthlyExpenses = useMemo(() => {
    const allExpenses: MonthlyLedgerExpense[] = StorageService.getItem('mylife_monthly_ledger_expenses', []);
    return allExpenses
      .filter((e) => e.yearMonth === currentMonth)
      .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  }, [currentMonth]);

  const monthlyIncome = useMemo(() => {
    const allIncomes: MonthlyLedgerIncome[] = StorageService.getItem('mylife_monthly_ledger_incomes', []);
    return allIncomes
      .filter((i) => i.yearMonth === currentMonth)
      .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
  }, [currentMonth]);

  // Tasks for Today
  const todayTasksList = useMemo(() => {
    return tasks.filter((t) => !t.dueDate || t.dueDate === todayStr);
  }, [tasks, todayStr]);

  const pendingTasks = todayTasksList.filter((t) => t.status === 'pending');
  const completedTasks = todayTasksList.filter((t) => t.status === 'completed');

  // Pinned & Recent Notes (Excluding Trashed)
  const recentNotes = useMemo(() => {
    return notes
      .filter((n) => !n.isTrashed)
      .sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
      })
      .slice(0, 3);
  }, [notes]);

  // Habits for today
  const todayHabitLogs = habitLogs.filter((hl) => hl.date === todayStr && hl.completed);
  const completedHabitsCount = todayHabitLogs.length;

  // Active Year Goals
  const activeGoals = useMemo(() => {
    return (yearlyGoals || []).filter((g) => g.status !== 'completed').slice(0, 3);
  }, [yearlyGoals]);

  // Top / Pinned Resources
  const topResources = useMemo(() => {
    return (resources || [])
      .sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return 0;
      })
      .slice(0, 4);
  }, [resources]);

  return (
    <div className="space-y-6 animate-fade-in pb-16">
      {/* Top Banner / Welcome with Quick Actions */}
      <div className="relative overflow-hidden rounded-3xl bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 p-6 sm:p-8 shadow-xs transition-all">
        {/* Glow ambient spots */}
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-72 h-72 bg-emerald-500/10 dark:bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 -mb-10 w-72 h-72 bg-teal-500/5 dark:bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-1.5 max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-xs font-bold border border-emerald-500/20">
              <Calendar className="w-3.5 h-3.5" />
              <span>{formattedDate}</span>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900 dark:text-white pt-1">
              {greeting}, {user?.name ? user.name.split(' ')[0] : 'Tahsin'}! 👋
            </h1>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
              Your personal dashboard is synced. Track finances, execute daily tasks, and manage notes effortlessly.
            </p>
          </div>
        </div>
      </div>

      {/* KPI Metric Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Net Balance */}
        <div
          onClick={() => setCurrentPage('finance')}
          className="p-5 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs transition-all hover:border-emerald-500/50 cursor-pointer group"
        >
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-2.5">
            <span className={`text-xs font-bold uppercase tracking-wider ${isBn ? 'font-bangla' : ''}`}>
              {isBn ? 'সর্বমোট ওয়ালেট ব্যালেন্স' : 'Total Accounts Balance'}
            </span>
            <div className="w-9 h-9 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Wallet className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight font-mono">
            ৳ {totalBalance.toLocaleString('en-US')}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 flex items-center justify-between">
            <span>{accounts.length} {isBn ? 'টি ওয়ালেট সক্রিয়' : 'active wallets'}</span>
            <span className="font-bold text-emerald-600 dark:text-emerald-400 inline-flex items-center">
              {isBn ? 'সবগুলো দেখুন' : 'View All'} <ChevronRight className="w-3 h-3 ml-0.5" />
            </span>
          </div>
        </div>

        {/* Today's Tasks */}
        <div
          onClick={() => setCurrentPage('tasks')}
          className="p-5 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs transition-all hover:border-blue-500/50 cursor-pointer group"
        >
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-2.5">
            <span className={`text-xs font-bold uppercase tracking-wider ${isBn ? 'font-bangla' : ''}`}>
              {isBn ? 'আজকের টাস্কসমূহ' : "Today's Tasks"}
            </span>
            <div className="w-9 h-9 rounded-2xl bg-blue-500/10 text-blue-500 flex items-center justify-center group-hover:scale-110 transition-transform">
              <CheckSquare className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            {completedTasks.length} / {todayTasksList.length}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 flex items-center justify-between">
            <span>{pendingTasks.length} {isBn ? 'টি কাজ বাকি' : 'tasks remaining'}</span>
            <span className="font-bold text-blue-600 dark:text-blue-400 inline-flex items-center">
              {isBn ? 'টাস্ক খুলুন' : 'Open Tasks'} <ChevronRight className="w-3 h-3 ml-0.5" />
            </span>
          </div>
        </div>

        {/* Active Notes */}
        <div
          onClick={() => setCurrentPage('notes')}
          className="p-5 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs transition-all hover:border-purple-500/50 cursor-pointer group"
        >
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-2.5">
            <span className={`text-xs font-bold uppercase tracking-wider ${isBn ? 'font-bangla' : ''}`}>
              {isBn ? 'সংরক্ষিত নোট' : 'Saved Notes'}
            </span>
            <div className="w-9 h-9 rounded-2xl bg-purple-500/10 text-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FileText className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            {notes.filter((n) => !n.isTrashed).length}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 flex items-center justify-between">
            <span>{notes.filter((n) => n.pinned && !n.isTrashed).length} {isBn ? 'টি পিন করা' : 'pinned'}</span>
            <span className="font-bold text-purple-600 dark:text-purple-400 inline-flex items-center">
              {isBn ? 'নোট দেখুন' : 'Notes'} <ChevronRight className="w-3 h-3 ml-0.5" />
            </span>
          </div>
        </div>

        {/* Habits Track */}
        <div
          onClick={() => setCurrentPage('habits')}
          className="p-5 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs transition-all hover:border-amber-500/50 cursor-pointer group"
        >
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-2.5">
            <span className={`text-xs font-bold uppercase tracking-wider ${isBn ? 'font-bangla' : ''}`}>
              {isBn ? 'হ্যাবিট ট্র্যাকার' : 'Habits Routine'}
            </span>
            <div className="w-9 h-9 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Flame className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            {completedHabitsCount} / {habits.length}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 flex items-center justify-between">
            <span>{isBn ? 'আজকে সম্পন্ন' : 'Done today'}</span>
            <span className="font-bold text-amber-600 dark:text-amber-400 inline-flex items-center">
              {isBn ? 'হ্যাবিটস' : 'Habits'} <ChevronRight className="w-3 h-3 ml-0.5" />
            </span>
          </div>
        </div>
      </div>

      {/* Main Widgets: 2 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT COLUMN: Tasks & Finance (7 cols) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Today's Tasks Box */}
          <div className="p-6 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
                  <CheckSquare className="w-4 h-4" />
                </div>
                <div>
                  <h3 className={`text-sm font-bold text-slate-900 dark:text-white ${isBn ? 'font-bangla' : ''}`}>
                    {isBn ? 'আজকের কাজের তালিকা' : "Today's Tasks"}
                  </h3>
                  <p className="text-[11px] text-slate-400">
                    {isBn ? '১-ক্লিকে টাস্ক বক্সে ক্লিক করে সম্পন্ন করুন' : 'Click task to complete in 1-click'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setCurrentPage('tasks')}
                className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
              >
                <span>{isBn ? 'সব দেখুন' : 'View All'}</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {todayTasksList.length === 0 ? (
              <div className="text-center py-6 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl p-4">
                <p className="text-xs text-slate-400 mb-2">
                  {isBn ? 'আজকের কোনো কাজ এখনও যুক্ত করা হয়নি।' : 'No tasks created for today.'}
                </p>
                <button
                  onClick={() => setActiveModal('task')}
                  className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold"
                >
                  {isBn ? '+ টাস্ক যোগ করুন' : '+ Add Task'}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Pending Tasks */}
                {pendingTasks.map((t) => (
                  <div
                    key={t.id}
                    onClick={() => {
                      toggleTask(t.id);
                      triggerConfetti();
                    }}
                    className="p-3 bg-slate-50 dark:bg-slate-800/60 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 border border-slate-200/60 dark:border-slate-700/60 rounded-2xl cursor-pointer transition-all flex items-center justify-between gap-3 group"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Circle className="w-4 h-4 text-slate-400 group-hover:text-blue-500 flex-shrink-0" />
                      <span className="text-xs font-bold text-slate-900 dark:text-white truncate">
                        {t.title}
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-slate-400">
                      {isBn ? 'অসম্পন্ন' : 'Pending'}
                    </span>
                  </div>
                ))}

                {/* Completed Tasks */}
                {completedTasks.slice(0, 2).map((t) => (
                  <div
                    key={t.id}
                    onClick={() => toggleTask(t.id)}
                    className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-2xl cursor-pointer transition-all flex items-center justify-between gap-3 opacity-75"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-xs font-bold text-slate-500 line-through truncate">
                        {t.title}
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                      {isBn ? 'সম্পন্ন' : 'Done'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Monthly Finance Summary Box */}
          <div className="p-6 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                  <WalletCards className="w-4 h-4" />
                </div>
                <div>
                  <h3 className={`text-sm font-bold text-slate-900 dark:text-white ${isBn ? 'font-bangla' : ''}`}>
                    {isBn ? 'মাসিক ফাইন্যান্স হিসাব' : 'Monthly Finance Hub'}
                  </h3>
                  <p className="text-[11px] text-slate-400">
                    {isBn ? 'ব্যক্তিগত, সাংগঠনিক ও বিজনেস স্প্রেডশিট' : 'Personal, Organization, and Business Ledgers'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setCurrentPage('finance')}
                className="text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1"
              >
                <span>{isBn ? 'হিসাব খাতা খুলুন' : 'Open Sheets'}</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60">
                <span className={`text-[10px] font-bold text-slate-400 uppercase block ${isBn ? 'font-bangla' : ''}`}>
                  {isBn ? 'চলতি মাসে মোট খরচ' : 'Monthly Expense'}
                </span>
                <span className="text-base font-black text-rose-600 dark:text-rose-400 font-mono mt-0.5 block">
                  ৳ {monthlyExpenses.toLocaleString('en-US')}
                </span>
              </div>

              <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60">
                <span className={`text-[10px] font-bold text-slate-400 uppercase block ${isBn ? 'font-bangla' : ''}`}>
                  {isBn ? 'চলতি মাসে মোট আয়' : 'Monthly Income'}
                </span>
                <span className="text-base font-black text-emerald-600 dark:text-emerald-400 font-mono mt-0.5 block">
                  ৳ {monthlyIncome.toLocaleString('en-US')}
                </span>
              </div>

              <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60 col-span-2 sm:col-span-1">
                <span className={`text-[10px] font-bold text-slate-400 uppercase block ${isBn ? 'font-bangla' : ''}`}>
                  {isBn ? 'আমার অ্যাকাউন্টস' : 'My Accounts'}
                </span>
                <span className="text-base font-black text-teal-600 dark:text-teal-400 font-mono mt-0.5 block">
                  {accounts.length} {isBn ? 'টি অ্যাকাউন্ট' : 'Accounts'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Notes & Goals (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          {/* Recent Notes Box */}
          <div className="p-6 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <h3 className={`text-sm font-bold text-slate-900 dark:text-white ${isBn ? 'font-bangla' : ''}`}>
                    {isBn ? 'সাম্প্রতিক নোটস' : 'Recent Notes'}
                  </h3>
                  <p className="text-[11px] text-slate-400">
                    {isBn ? 'আপনার লেখা নোটসমূহের তালিকা' : 'Quick view of your notes'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setCurrentPage('notes')}
                className="text-xs font-bold text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1"
              >
                <span>{isBn ? 'সব দেখুন' : 'View All'}</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {recentNotes.length === 0 ? (
              <div className="text-center py-6 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl p-4">
                <p className="text-xs text-slate-400 mb-2">
                  {isBn ? 'কোনো নোট তৈরি করা হয়নি।' : 'No notes created yet.'}
                </p>
                <button
                  onClick={() => setActiveModal('note')}
                  className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold"
                >
                  {isBn ? '+ নতুন নোট' : '+ Add Note'}
                </button>
              </div>
            ) : (
              <div className="space-y-2.5">
                {recentNotes.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => setCurrentPage('notes')}
                    className="p-3.5 bg-slate-50 dark:bg-slate-800/60 hover:bg-purple-50/40 dark:hover:bg-purple-950/20 border border-slate-200/60 dark:border-slate-700/60 rounded-2xl cursor-pointer transition-all space-y-1"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-xs font-bold text-slate-900 dark:text-white truncate">
                        {n.title}
                      </h4>
                      {n.isLocked && <Lock className="w-3 h-3 text-amber-500 flex-shrink-0" />}
                    </div>
                    {n.content && !n.isLocked && (
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                        {n.content}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Goals & Plans Box */}
          <div className="p-6 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-teal-500/10 text-teal-500 flex items-center justify-center">
                  <Target className="w-4 h-4" />
                </div>
                <div>
                  <h3 className={`text-sm font-bold text-slate-900 dark:text-white ${isBn ? 'font-bangla' : ''}`}>
                    {isBn ? 'গোল ও বাৎসরিক রোডম্যাপ' : 'Goals & Plans'}
                  </h3>
                  <p className="text-[11px] text-slate-400">
                    {isBn ? `${currentYear} সালের লক্ষ্যসমূহ` : `Year ${currentYear} roadmap`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setCurrentPage('goals')}
                className="text-xs font-bold text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1"
              >
                <span>{isBn ? 'সব গোল' : 'All Goals'}</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {activeGoals.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">
                {isBn ? `${currentYear} সালের কোনো সক্রিয় গোল নেই।` : `No active goals set for ${currentYear}.`}
              </p>
            ) : (
              <div className="space-y-2.5">
                {activeGoals.map((g) => (
                  <div
                    key={g.id}
                    onClick={() => setCurrentPage('goals')}
                    className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60 hover:border-teal-500/40 cursor-pointer transition-all space-y-1"
                  >
                    <h4 className="font-bold text-xs text-slate-900 dark:text-white truncate">
                      {g.title}
                    </h4>
                    {g.description && (
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                        {g.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
